/**
 * EthicsGlobal Widget - Isolated Form Component
 *
 * A self-contained form component that doesn't depend on react-router
 * and receives all configuration via props instead of localStorage.
 */

import { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import { createWidgetApiClient } from "./widget-api";
import { formStarter } from "../components/Form/PreviewForm/formStarter";
import { WidgetPreviewForm } from "./WidgetPreviewForm";
import I18nText from "../components/Form/PreviewForm/i18n";
import { setDomain, setLang as setStoreLang, setAppInfo } from "./widget-store";

/**
 * WidgetFormIsolated - Isolated form component for the embeddable widget
 *
 * @param {Object} props
 * @param {string} props.domain - The tenant domain (required)
 * @param {string} props.lang - Language code (default: 'es')
 * @param {string} props.theme - Theme: 'light' or 'dark' (default: 'light')
 * @param {string} props.businessUnit - Optional business unit (deprecated, use formParams.bu)
 * @param {Object} props.formParams - Pre-select catalogues { bu: 'key', rc: 'key' }
 * @param {string} props.formIdentifierName - Optional form identifier name
 * @param {Function} props.onReady - Callback when form is ready
 * @param {Function} props.onSubmit - Callback when form is submitted
 * @param {Function} props.onError - Callback on error
 * @param {Object} props.apiClient - Optional pre-configured API client
 * @param {Object} props.config - Additional config to pass to PreviewForm
 */
export const WidgetFormIsolated = ({
  domain,
  lang: propLang = 'es',
  theme = 'light',
  businessUnit,
  formParams = {},
  formIdentifierName,
  onReady,
  onSubmit,
  onError,
  apiClient: externalApiClient,
  config = {}
}) => {
  const dispatch = useDispatch();
  const { content } = useSelector(state => state.app || {});

  // Use prop language or fall back to store
  const lang = propLang || 'es';

  // State
  const [formLoaded, setFormLoaded] = useState(null);
  const [formIdentifier, setFormIdentifier] = useState(null);
  const [formDescription, setFormDescription] = useState(null);
  const [steps, setSteps] = useState(null);
  const [schemaState, setSchemaState] = useState(null);
  const [formData, setFormData] = useState(null);
  const [isValid, setIsValid] = useState(null);
  const [constraints, setConstraints] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create API client instance
  const apiClient = externalApiClient || createWidgetApiClient(domain);

  // Set domain in store (namespaced localStorage handled by widget-store)
  useEffect(() => {
    if (domain) {
      dispatch(setDomain(domain));
      // Also set in namespaced localStorage for API calls
      try {
        localStorage.setItem('eg_domain', domain);
        // Keep backward compatibility with main app
        localStorage.setItem('origin_domain', domain);
      } catch (e) {
        // localStorage not available
      }
    }
  }, [domain, dispatch]);

  // Set language in store
  useEffect(() => {
    if (lang) {
      dispatch(setStoreLang(lang));
    }
  }, [lang, dispatch]);

  // Load form data
  useEffect(() => {
    const loadForm = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let formResponse;

        if (formIdentifierName) {
          // Load specific form by identifier
          formResponse = await apiClient.getDetailReportByName(lang, formIdentifierName);
        } else {
          // Load default current report form
          formResponse = await apiClient.getDetailCurrentReport(lang);
        }

        if (!formResponse) {
          throw new Error('No form data received from API');
        }

        setFormLoaded(formResponse);

        if (onReady) {
          onReady({ form: formResponse, lang, domain });
        }
      } catch (err) {
        console.error('[EthicsGlobal Widget] Error loading form:', err);
        setError(err);
        if (onError) {
          onError(err);
        }
      }
    };

    if (domain) {
      loadForm();
    }
  }, [domain, lang, formIdentifierName]);

  // Process form data after loading
  useEffect(() => {
    const processForm = async () => {
      if (!formLoaded) return;

      setIsLoading(true);

      try {
        const {
          initFormIdentifier,
          initSteps,
          initSchemaState,
          initFormData,
          initIsValid,
          initConstraints
        } = await formStarter(formLoaded, 'normal', null, lang);

        setFormIdentifier(initFormIdentifier);
        setSteps(initSteps);
        setSchemaState(initSchemaState);
        setFormData(initFormData);
        setIsValid(initIsValid);
        setConstraints(initConstraints);
        setIsLoading(false);
      } catch (err) {
        console.error('[EthicsGlobal Widget] Error processing form:', err);
        setError(err);
        if (onError) {
          onError(err);
        }
        setIsLoading(false);
      }
    };

    processForm();
  }, [formLoaded, lang]);

  // Handle form submission
  const handleFormSubmit = useCallback((data) => {
    if (onSubmit) {
      onSubmit(data);
    }
  }, [onSubmit]);

  // Determine theme class
  const themeClass = theme === 'dark' ? 'eg-theme-dark' : 'eg-theme-light';

  // Error state
  if (error) {
    return (
      <div className={`eg-widget ${themeClass}`} data-theme={theme}>
        <div className="eg-widget-container">
          <div className="eg-error">
            <div className="eg-error-icon">⚠️</div>
            <div className="eg-error-message">
              {I18nText({ lang, translation: 'widget_load_error' })}
            </div>
            <button
              className="eg-btn eg-btn-primary"
              onClick={() => window.location.reload()}
            >
              {I18nText({ lang, translation: 'widget_retry' })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`eg-widget ${themeClass}`} data-theme={theme}>
      <div className="eg-widget-container">
        {isLoading ? (
          <div className="eg-loading">
            <div className="eg-loading-spinner"></div>
            <div className="eg-loading-text">
              {I18nText({ lang, translation: 'widget_loading' })}
            </div>
          </div>
        ) : (
          <div className="eg-form-wrapper">
            <WidgetPreviewForm
              formIdentifier={formIdentifier}
              formDescription={formDescription}
              steps={steps}
              schemaState={schemaState}
              setSchemaState={setSchemaState}
              formData={formData}
              setFormData={setFormData}
              isValid={isValid}
              setIsValid={setIsValid}
              constraints={constraints}
              setConstraints={setConstraints}
              showButtons={true}
              stepClick={false}
              lang={lang}
              content={content}
              fullscreen={true}
              theme={theme}
              apiClient={apiClient}
              onSubmit={handleFormSubmit}
              onError={onError}
              businessUnit={businessUnit}
              formParams={formParams}
              {...config}
            />
          </div>
        )}
        <div className={`eg-progress-bar ${isLoading ? 'eg-progress-loading' : 'eg-progress-complete'}`}></div>
      </div>
    </div>
  );
};

export default WidgetFormIsolated;
